/*
 * Monedas con saldo seguido por la planilla.
 *
 * Cada moneda es un "bucket" de cálculo ('ARS' | 'USD' | 'CABLE') y arrastra
 * consigo: la etiqueta con la que se guarda en los movimientos (cauciones,
 * pases, operaciones, instrucciones), el campo del snapshot del custodio, el
 * campo de la colección legacy `saldos` y el código con el que la unidad viene
 * en el Excel del custodio.
 *
 * Centralizado acá porque antes eran ternarios `moneda === 'ARS' ? x : y`
 * repartidos por todo el código: con tres monedas eso ya no cierra.
 */

export const BUCKET_ARS = 'ARS';
export const BUCKET_USD = 'USD';
export const BUCKET_CABLE = 'CABLE';

// Orden canónico de presentación: pesos, dólar MEP, dólar cable.
export const MONEDA_BUCKETS = [BUCKET_ARS, BUCKET_USD, BUCKET_CABLE];

// Etiqueta con la que la moneda se guarda en los documentos de movimientos.
export const MONEDA_MOVIMIENTO = {
    [BUCKET_ARS]: 'Pesos',
    [BUCKET_USD]: 'USD Mep',
    [BUCKET_CABLE]: 'USD Cable',
};

// Título corto para encabezados de pestañas y secciones.
export const MONEDA_TITULO = {
    [BUCKET_ARS]: 'Pesos',
    [BUCKET_USD]: 'Dólares',
    [BUCKET_CABLE]: 'USD Cable',
};

// Campo del snapshot del custodio (`saldosSnapshots.comitentes[]`).
export const SNAPSHOT_SALDO_FIELD = {
    [BUCKET_ARS]: 'saldoARS',
    [BUCKET_USD]: 'saldoUSD',
    [BUCKET_CABLE]: 'saldoCable',
};

// Campo de la colección legacy `saldos` (último snapshot pisado por cada carga).
export const SALDO_INICIAL_FIELD = {
    [BUCKET_ARS]: 'saldoInicialARS',
    [BUCKET_USD]: 'saldoInicialUSD',
    [BUCKET_CABLE]: 'saldoInicialCable',
};

// Apertura del día estampada en la proyección por comitente (`saldosComitente`).
export const SALDO_INICIAL_DIA_FIELD = {
    [BUCKET_ARS]: 'saldoInicialDiaARS',
    [BUCKET_USD]: 'saldoInicialDiaUSD',
    [BUCKET_CABLE]: 'saldoInicialDiaCable',
};

// Campo donde se persiste la conciliación entre snapshots.
export const DIFERENCIAS_FIELD = {
    [BUCKET_ARS]: 'diferenciasARS',
    [BUCKET_USD]: 'diferenciasUSD',
    [BUCKET_CABLE]: 'diferenciasCable',
};

/*
 * Códigos de la columna "Unidad" del Excel del custodio. El cable viene como
 * "USDC"; las variantes se aceptan normalizadas (sin espacios ni guiones) para
 * no perder filas si el export cambia de formato.
 */
const UNIDAD_CUSTODIO = {
    ARS: BUCKET_ARS,
    USD: BUCKET_USD,
    USDC: BUCKET_CABLE,
    USDCABLE: BUCKET_CABLE,
    CABLE: BUCKET_CABLE,
};

/*
 * Bucket de una unidad del Excel del custodio, o null si no es una moneda que
 * la planilla siga (títulos, otras unidades).
 */
export const bucketDeUnidadCustodio = (unidad) => {
    const clave = String(unidad ?? '').toUpperCase().replace(/[\s\-_.]/g, '');
    return UNIDAD_CUSTODIO[clave] || null;
};

// Etiqueta de movimiento -> bucket. Acepta también el bucket ya resuelto.
const MONEDA_A_BUCKET = {
    Pesos: BUCKET_ARS,
    ARS: BUCKET_ARS,
    'USD Mep': BUCKET_USD,
    USD: BUCKET_USD,
    // Contado con liquidación y cable son lo mismo: misma plaza de liquidación.
    'USD Cable': BUCKET_CABLE,
    'USD CCL': BUCKET_CABLE,
    CABLE: BUCKET_CABLE,
};

/*
 * Bucket de cálculo de una moneda. Por defecto ARS, que es como se comportaba
 * el código antes de tener tres monedas.
 */
export const bucketDeMoneda = (moneda) => MONEDA_A_BUCKET[moneda] || BUCKET_ARS;

// true si la moneda (bucket o etiqueta) se expresa en pesos.
export const esPesos = (moneda) => bucketDeMoneda(moneda) === BUCKET_ARS;

// Etiquetas de movimiento de las monedas con saldo seguido.
export const MONEDAS_CON_SALDO = MONEDA_BUCKETS.map(b => MONEDA_MOVIMIENTO[b]);
